import { Currency } from "@redotech/money/currencies";
import { MoneyValue } from "@redotech/money/money-calculator";
import { ObjectId } from "bson";
import {
  ExchangeItemTotals,
  ProductSummaryTotals,
  ReturnSummaryTotals,
  SerializedExchangeItemTotals,
} from "./types";

/**
 * Looks up the totals for one exchange item. Pass `advancedExchangeItems._id`
 * for advanced exchanges, or the `ProductTotals.id` key for variant exchanges.
 */
export const getExchangeItemTotals = (
  returnTotals: ReturnSummaryTotals<Currency>,
  exchangeItemId: ObjectId | string,
): ExchangeItemTotals<Currency> | undefined => {
  const id = exchangeItemId.toString();
  return returnTotals.newOrderTotals?.exchangeItemTotalsList?.find(
    (item) => item.id === id,
  );
};

/**
 * The exchange lines the allocation engine settled, from a per-line totals
 * list: a draft's `exchangeItemTotals` or a return's `exchangeItemTotalsList`.
 * Empty for an unstamped return and wherever nothing settled, so a reader
 * falls through to the document's own price exactly as today.
 */
export const settledExchangeLines = (
  lines: readonly SerializedExchangeItemTotals[] | undefined,
): SerializedExchangeItemTotals[] =>
  (lines ?? []).filter(
    (line) => line.id !== undefined && line.settledBy?.length,
  );

/** `returnProductId` is a `ProductTotals.id` returnable item key, not an ObjectId. */
export const getProductTotals = (
  returnTotals: ReturnSummaryTotals<Currency>,
  returnProductId: string,
): ProductSummaryTotals<Currency> | undefined =>
  returnTotals.productTotalsList.find(
    (product) => product.id === returnProductId,
  );

/**
 * Total return-flow adjustments. Falls back to `totalNetAdjustmentValue` for
 * serialized totals predating `totalFlowAdjustments`.
 */
export const getTotalFlowAdjustments = (
  returnTotals: ReturnSummaryTotals<Currency>,
): MoneyValue<Currency> =>
  returnTotals.adjustmentTotals.totalFlowAdjustments ??
  returnTotals.adjustmentTotals.totalNetAdjustmentValue;
